import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import { Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import { Backdrop } from '@material-ui/core';

import i18n from 'i18next';

import LoginForm from 'Components/Login/LoginForm';
import VerificationForm from 'Components/Login/VerificationForm';

import Fetch from 'helpers/fetch';
import { buildUrl } from 'helpers/misc';
import { AUTHENTICATE, RESENDCODE } from 'constants/apis';
import { Loader } from 'Components/index';

import { getRegionData } from 'constants/regions';

const processRedirectToMyAccount = (ErrorCode) => {
  if (
    (ErrorCode && ErrorCode === 111) ||
    ErrorCode === 107 ||
    ErrorCode === 112
  ) {
    localStorage.setItem('RedirectToAccounts', true);
  }
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

const Login = ({ onLogin }) => {
  const classes = useStyles();

  const [state, setState] = useState({
    hasError: false,
    errorMessage: '',
    verificationCode: '',
    twoFactorKey: '',
    isVerificationCodeRequired: false,
    codeSentMessage: '',
    email: '',
    isSubmitting: false,
  });

  const handleResendCode = (e) => {
    e.preventDefault();

    const params = {
      username: state.email.trim(),
    };

    setState({
      ...state,
      isSubmitting: true,
      hasError: false,
      errorMessage: '',
    });

    Fetch.Get(buildUrl(RESENDCODE, params))
      .then((response) => {
        response.json().then((data) => {
          setState((prevState) => ({
            ...prevState,
            isSubmitting: false,
            hasError: false,
            twoFactorKey: response.headers.get('TwoFactorKey'),
            codeSentMessage: data,
          }));
        });
      })
      .catch((err) => {
        err.response.json().then((data) =>
          setState({
            ...state,
            errorMessage: data.Message,
            isSubmitting: false,
            hasError: true,
          }),
        );
      });
  };

  const handleLoginSubmit = (values) => {
    localStorage.setItem('Region', values.region);

    setState({
      ...state,
      isSubmitting: true,
      hasError: false,
      errorMessage: '',
      codeSentMessage: '',
      email: values.email.trim(),
      confirmationCode: '',
    });

    const basic = btoa(`${values.email.trim()}:${values.password.trim()}`);
    const confirmationCode = values.code.trim();
    const twoFactorKey = state.twoFactorKey;

    let requestOptions = {
      headers: {
        Authorization: `Basic ${basic}`,
        'Cache-Control': 'no-cache',
        Region: `${getRegionData(localStorage.getItem('Region')).value}`,
        Pragma: 'no-cache',
      },
    };

    if (twoFactorKey !== '') {
      requestOptions = {
        headers: {
          ...requestOptions.headers,
          ConfirmationCode: confirmationCode,
          TwoFactorKey: twoFactorKey,
        },
      };
    }

    return Fetch.Get(AUTHENTICATE, requestOptions)
      .then(async (response) => {
        setState({ ...state, isSubmitting: false });

        const data = await response.json();

        localStorage.setItem(
          'IdentityToken',
          response.headers.get('IdentityToken'),
        );
        localStorage.setItem(
          'RefreshToken',
          response.headers.get('RefreshToken'),
        );

        processRedirectToMyAccount(data.ErrorCode);
        onLogin(data);
      })
      .catch(({ response }) => {
        const errorCodes = [105, 106, 107];
        const contentType = response && response.headers.get('content-type');

        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response.json().then((err) => {
            processRedirectToMyAccount(err.ErrorCode);

            if (errorCodes.includes(err.ErrorCode)) {
              return setState((prevState) => ({
                ...prevState,
                isVerificationCodeRequired: true,
                errorMessage: err.ErrorMessage,
                twoFactorKey: response.headers.get('TwoFactorKey'),
                hasError: true,
                isSubmitting: false,
              }));
            }

            return setState((prevState) => ({
              ...prevState,
              errorMessage:
                err.ErrorMessage === undefined ? err : err.ErrorMessage,
              hasError: true,
              isSubmitting: false,
            }));
          });
        }

        return response.text().then((err) => {
          setState((prevState) => ({
            ...prevState,
            errorMessage: err,
            hasError: true,
            isSubmitting: false,
          }));
        });
      });
  };

  const handleBackToLoginClick = () => {
    setState({
      ...state,
      hasError: false,
      isSubmitting: false,
      email: '',
      isVerificationCodeRequired: false,
      twoFactorKey: '',
    });
  };

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    if (name && name === 'region') {
      localStorage.setItem('Region', value);
      const { redirectToUrl } = getRegionData(value);
      if (redirectToUrl) {
        setState({
          ...state,
          isSubmitting: true,
        });
        window.location.href = redirectToUrl;
      }
    }
  };

  return (
    <Fragment>
      {!state.isVerificationCodeRequired ? (
        <Formik
          onSubmit={handleLoginSubmit}
          initialValues={{
            region: 0,
            email: '',
            password: '',
            code: '',
          }}
          validationSchema={yup.object().shape({
            region: yup
              .string()
              .matches(/^([^0-9]*)$/, i18n.t('login:form:region:error')),
            email: yup
              .string()
              .trim()
              .email(i18n.t('login:form:email:error'))
              .required(i18n.t('login:form:email:error')),
            password: yup
              .string()
              .required(i18n.t('login:form:password:error')),
          })}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {(props) => (
            <LoginForm
              {...props}
              onChange={handleOnChange}
              errorMessage={!state.hasError ? '' : state.errorMessage}
            />
          )}
        </Formik>
      ) : (
        <Formik
          onSubmit={handleLoginSubmit}
          initialValues={{
            code: '',
          }}
          validationSchema={yup.object().shape({
            code: yup.string().required(i18n.t('verification:form:code:error')),
          })}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {(props) => (
            <VerificationForm
              {...props}
              codeSentMessage={state.codeSentMessage}
              errorMessage={state.errorMessage}
              onResendCode={handleResendCode}
              onBackToLoginClick={handleBackToLoginClick}
            />
          )}
        </Formik>
      )}
      <Backdrop className={classes.backdrop} open={state.isSubmitting}>
        <Loader />
      </Backdrop>
    </Fragment>
  );
};

Login.propTypes = {
  onLogin: PropTypes.func,
};

export default Login;



// WEBPACK FOOTER //
// ./src/containers/Login/Login.js